import { DomUtils } from './utils';

class ScrollAnimations {
    constructor() {
        this.DOM = {
            body: document.body
        };

        this.viewportWidth = DomUtils.getViewportWidth();
        this.vw = this.viewportWidth / 100;

        this.bigTitleClipPath();
        this.titleClipPath();
        this.triggerParallax();
        this.blockTransformOverlap();
        this.triggerElementsInView();
        this.triggerElementsInViewLater();

        // Content loaded in later (e.g. Vue-rendered cards) dispatches this once it's in the DOM,
        // so pick up any new .trigger-parallax elements that didn't exist on initial load
        document.addEventListener('refreshScrollTrigger', () => {
            this.triggerParallax();
        });
    }

    titleClipPath() {
        const titles = document.querySelectorAll('.js-title-clip');

        if (titles.length < 1) {
            return;
        }

        this.splitTitleLines(titles);

        ScrollTrigger.batch(titles, {
            // markers: true,
            start: 'top bottom-=10%',
            onEnter: (titles) => {
                titles.forEach((title) => {
                    this.addTitleInView(title);
                });
            },
            onEnterBack: (titles) => {
                titles.forEach((title) => {
                    this.addTitleInView(title);
                });
            }
        });
    }

    addTitleInView(title) {
        // Registered after splitTitleLines' document.fonts.ready callbacks, so this always
        // runs after the title has been split, even if it was already in view on load
        document.fonts.ready.then(() => {
            const delay = parseFloat(title.dataset.delay) || 0;

            setTimeout(() => {
                title.classList.add('in-view');
            }, delay);
        });
    }

    splitTitleLines(titles) {
        titles.forEach((title) => {
            if (title.querySelector('line')) {
                return;
            }

            const splitWords = title.hasAttribute('data-split-words');

            document.fonts.ready.then(() => {
                SplitText.create(title, {
                    type: splitWords ? 'lines,words' : 'lines',
                    wordsClass: 'js-split-word',
                    autoSplit: true,
                    onSplit: (self) => {
                        let wordIndex = 0;

                        self.lines.forEach((lineEl) => {
                            const line = document.createElement('line');
                            const container = splitWords ? line : document.createElement('span');

                            container.innerHTML = lineEl.innerHTML;

                            if (splitWords) {
                                container.querySelectorAll('.js-split-word').forEach((wordEl) => {
                                    const word = document.createElement('word');

                                    word.innerHTML = wordEl.innerHTML;
                                    word.style.setProperty('--index', wordIndex++);
                                    wordEl.replaceWith(word);
                                });
                            }

                            // SplitText can leave empty leftover wrapper tags (e.g. <highlight>) when it
                            // splits an inline element across a line break
                            container.querySelectorAll('*').forEach((el) => {
                                if (!el.closest('svg') && !el.textContent.trim() && el.children.length === 0) {
                                    el.remove();
                                }
                            });

                            if (!splitWords) {
                                line.appendChild(container);
                            }

                            lineEl.replaceWith(line);
                        });

                        // SplitText's auto aria-label includes SVG text content, so recompute it without SVGs
                        if (title.querySelector('svg')) {
                            const clone = title.cloneNode(true);
                            clone.querySelectorAll('svg').forEach((svg) => svg.remove());
                            title.setAttribute('aria-label', clone.textContent.trim().replace(/\s+/g, ' '));
                        }

                        ScrollTrigger.refresh();
                    }
                });
            });
        });
    }

    bigTitleClipPath() {
        const titles = document.querySelectorAll('.js-big-title-clip');

        if (titles.length < 1) {
            return;
        }
        titles.forEach((title) => {
            const parts = title.querySelectorAll('line');

            parts.forEach((part) => {
                gsap.fromTo(
                    part,
                    { '--progress': '0' },
                    {
                        '--progress': '1',
                        ease: 'none',
                        scrollTrigger: {
                            // markers: true,
                            trigger: part,
                            start: 'top bottom+=20%',
                            end: 'top bottom-=60%',
                            invalidateOnRefresh: true,
                            scrub: 0.3
                        }
                    }
                );
            });
        });
    }

    triggerParallax() {
        // Respect users who prefer reduced motion
        const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (prefersReducedMotion) {
            return;
        }

        const triggerElements = document.querySelectorAll('.trigger-parallax:not(.js-parallax-init)');
        if (!triggerElements.length) {
            return;
        }

        triggerElements.forEach((element) => {
            element.classList.add('js-parallax-init');

            let parallaxItem = element.querySelectorAll('img, video');
            let scale = 1.085;
            let transform = element.dataset.parallaxTransform ? Number(element.dataset.parallaxTransform) : 7;
            let scrub = element.dataset.parallaxScrub ? Number(element.dataset.parallaxScrub) : 1;
            const isParallaxTop = element.classList.contains('parallax-top');
            const isInverted = element.classList.contains('parallax-inverted');

            if (isInverted) {
                scale = 1.14;
            }
            if (!parallaxItem.length) {
                parallaxItem = element;
                scale = 1;
            }

            // Refresh ScrollTrigger once any lazy-loaded images finish loading,
            // so start/end positions are recalculated against final layout
            if (parallaxItem !== element) {
                parallaxItem.forEach((media) => {
                    if (media.tagName === 'IMG' && !media.complete) {
                        media.addEventListener('load', () => ScrollTrigger.refresh(), { once: true });
                    }
                    if (media.tagName === 'VIDEO' && media.readyState < 1) {
                        media.addEventListener('loadedmetadata', () => ScrollTrigger.refresh(), { once: true });
                    }
                });
            }

            // Give mobile a lighter (or disabled) version to avoid scroll jank
            ScrollTrigger.matchMedia({
                '(min-width: 769px)': () => {
                    buildTween(transform, scale);
                },
                '(max-width: 768px)': () => {
                    // Halve the movement on mobile; change to `return;` to disable entirely
                    buildTween(transform / 2, scale);
                }
            });

            function buildTween(transformAmount, scaleAmount) {
                gsap.fromTo(
                    parallaxItem,
                    {
                        y: isParallaxTop ? '0%' : isInverted ? `${transformAmount}%` : `-${transformAmount}%`,
                        scale: scaleAmount
                    },
                    {
                        y: isInverted ? `-${transformAmount}%` : `${transformAmount}%`,
                        scale: scaleAmount,
                        ease: 'none',
                        scrollTrigger: {
                            trigger: element,
                            start: isParallaxTop ? 'top top' : 'top bottom',
                            end: 'bottom top',
                            scrub: scrub,
                            invalidateOnRefresh: true,
                            onEnter: () => gsap.set(parallaxItem, { willChange: 'transform' }),
                            onEnterBack: () => gsap.set(parallaxItem, { willChange: 'transform' }),
                            onLeave: () => gsap.set(parallaxItem, { willChange: 'auto' }),
                            onLeaveBack: () => gsap.set(parallaxItem, { willChange: 'auto' })
                        }
                    }
                );
            }
        });
    }

    blockTransformOverlap() {
        const elements = document.querySelectorAll('.js-block-overlap');

        if (elements.length < 1) {
            return;
        }

        elements.forEach((element) => {
            if (!element.matches('.js-block-overlap:has(+ .js-block-overlap), .js-block-overlap:last-child')) {
                return;
            }

            gsap.to(element, {
                y: '25%',
                scrollTrigger: {
                    // markers: true,
                    trigger: element,
                    start: 'top top',
                    end: 'bottom top',
                    scrub: 0.1
                }
            });
        });
    }

    triggerElementsInView() {
        if (!document.querySelectorAll('.trigger-in-view')) {
            return;
        }

        ScrollTrigger.batch('.trigger-in-view', {
            // markers: true,
            start: 'center bottom',
            end: 'bottom top',
            onEnter: (elements) => {
                elements.forEach((element) => {
                    element.classList.add('in-view');
                });
            },
            onEnterBack: (elements) => {
                elements.forEach((element) => {
                    element.classList.add('in-view');
                });
            }
        });
    }

    triggerElementsInViewLater() {
        if (!document.querySelectorAll('.trigger-in-view-later')) {
            return;
        }

        ScrollTrigger.batch('.trigger-in-view-later', {
            // markers: true,
            start: 'top 70%',
            end: 'bottom top',
            onEnter: (elements) => {
                elements.forEach((element) => {
                    element.classList.add('in-view');
                });
            },
            onEnterBack: (elements) => {
                elements.forEach((element) => {
                    element.classList.add('in-view');
                });
            }
        });
    }
}
new ScrollAnimations();
